import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, filter } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AppSidebarComponent } from '../app-sidebar/app-sidebar.component';
import { AppHeaderComponent } from '../app-header/app-header.component';

const SIDEBAR_LG_QUERY = '(max-width: 1023.98px)';
const COLLAPSED_KEY = 'ftg-sidebar-collapsed';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [MatSidenavModule, RouterOutlet, AppSidebarComponent, AppHeaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-layout.component.html',
  host: { class: 'block h-screen' },
})
export class AppLayoutComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe([SIDEBAR_LG_QUERY]).pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  /** Único origen de verdad del estado abierto/cerrado en móvil — sincronizado en ambas
   * direcciones con MatSidenav vía `(openedChange)`, nunca mutado de forma imperativa. */
  readonly sidenavOpened = signal(true);
  readonly collapsed = signal(this.loadCollapsed());

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isMobile()) {
          this.sidenavOpened.set(false);
        }
      });
  }

  ngOnInit(): void {
    if (this.isMobile()) {
      this.sidenavOpened.set(false);
    }
  }

  toggleSidenav(): void {
    this.sidenavOpened.update((v) => !v);
  }

  onOpenedChange(opened: boolean): void {
    this.sidenavOpened.set(opened);
  }

  setCollapsed(collapsed: boolean): void {
    this.collapsed.set(collapsed);
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }

  private loadCollapsed(): boolean {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  }
}
